import React, { useEffect, useState } from 'react';
import AdminLayout from '../../../components/layout/AdminLayout';
import Card from '../../../components/common/Card';
import ListingTable from '../../../components/tables/ListingTable';
import Button from '../../../components/common/Button';
import FormField from '../../../components/common/FormField';
import { useListingApi } from '../../../hooks/useListingApi';
import AddNewButton from '../../../components/common/AddButton';
import { confirmDelete } from '../../../utils/sweetAlertUtils';
import { ListingDTO } from '../../../services/apiClient';
import { useNavigate } from 'react-router-dom';
import { useListingTypes } from '../../../hooks/useListingTypeApi';
import { useListingStatuses } from '../../../hooks/useListingStatusApi';
import { useSearchState } from '../../../utils/searchStateUtils/useSearchState';

const ListingList: React.FC = () => {
  const { search, remove, isLoading, error } = useListingApi();
  const [items, setItems] = useState<ListingDTO[]>([]);
  
  // Search state persistence
  const {
    currentPage,
    pageSize,
    sortBy,
    sortDescending,
    searchFilters,
    setCurrentPage,
    setPageSize,
    setSortBy,
    setSortDescending,
    setSort,
    setSearchFilters,
    showAdvanced,
    setShowAdvanced,
    clearSearchState,
  } = useSearchState('listings', {
    currentPage: 1,
    pageSize: 10,
    sortBy: 'CreatedUtc',
    sortDescending: true,
    searchFilters: {},
    showAdvanced: false,
  });
  
  const [totalRecords, setTotalRecords] = useState(0);
  const [listingTypeOptions, setListingTypeOptions] = useState<
    { value: string; label: string }[]
  >([]);
  const [listingStatusOptions, setlistingStatusOptions] = useState<
    { value: string; label: string }[]
  >([]);

  const navigate = useNavigate();
  const breadcrumbs = [
    { label: 'Admin', href: '#' },
    { label: 'Listings', href: '#' },
  ];

  const { data: listingTypesData = [], isLoading: isLoadingListingTypes } = useListingTypes();
  const { data: listingStatusesData = [], isLoading: isLoadingListingStatuses } = useListingStatuses();

  const loadDropdownOptions = async () => {
    try {
      setListingTypeOptions(
        listingTypesData.map(type => ({
          value: type.id || '',
          label: type.title || ''
        }))
      );

      setlistingStatusOptions(
        listingStatusesData.map(status => ({
          value: status.id || '',
          label: status.title || ''
        }))
      );
    } catch (error) {
      console.error('Error loading dropdown options:', error);
    }
  };

  const fetchItems = async (page: number, size: number, filters: any) => {
    try {
      const response = await search({
        filters,
        page,
        pageSize: size,
        sortBy: sortBy,
        sortDescending: sortDescending,
      });
      setItems(response?.data?.results || []);
      setTotalRecords(response?.data?.totalCount || 0);
    } catch (error) {
      console.error('Error fetching Listings:', error);
    }
  };

  useEffect(() => {
    fetchItems(currentPage, pageSize, searchFilters);
  }, [currentPage, pageSize, sortBy, sortDescending]);

  useEffect(() => {
    loadDropdownOptions();
  }, []);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchItems(1, pageSize, searchFilters);
  };

  const handleClearSearch = () => {
    clearSearchState();
  };

  const handleDelete = async (dto: ListingDTO) => {
    const confirmed = await confirmDelete();
    if (!confirmed) return;

    try {
      await remove(dto.id || '');
      await fetchItems(currentPage, pageSize, searchFilters);
    } catch (error) {
      console.error('Error deleting Listing:', error);
    }
  };

  const handleSort = (sortKey: string) => {
    if (sortBy === sortKey) {
      setSort(sortKey, !sortDescending);
    } else {
      setSort(sortKey, true);
    }
  };

  return (
    <AdminLayout
      activeMenuItem="/admin/listings"
      breadcrumbs={breadcrumbs}
      allowedRoles={['Admin']}
      requiredPermission="Manage.Listing.View"
    >
      <Card
        title="Manage Listings"
        subtitle={`Total Records: ${totalRecords}`}
        headerActions={
          <div className="flex space-x-2">
            <Button
              type="button"
              variant="secondary"
              onClick={() => setShowAdvanced(!showAdvanced)}
            >
              {showAdvanced ? "Hide Advanced Search" : "Advanced Search"}
            </Button>
            <AddNewButton onClick={() => navigate('/admin/listings/create')} />
          </div>
        }
      >
        <form onSubmit={handleSearch} className="mb-4">
          {showAdvanced && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
              <FormField
                id="listingTypeId"
                label="Search by Listing Type"
                type="dropdown"
                value={searchFilters.listingTypeId || ''}
                onChange={(e) =>
                  setSearchFilters({
                    ...searchFilters,
                    listingTypeId: e.target.value,
                  })
                }
                placeholder="All Listing Types"
                options={listingTypeOptions}
              />
              <FormField
                id="name"
                label="Search by Name"
                value={searchFilters.name || ''}
                onChange={(e) =>
                  setSearchFilters({ ...searchFilters, name: e.target.value })
                }
                placeholder="Search by Name"
              />
              <FormField
                id="listingStatusId"
                label="Search by Listing Status"
                type="dropdown"
                value={searchFilters.listingStatusId || ''}
                onChange={(e) =>
                  setSearchFilters({
                    ...searchFilters,
                    listingStatusId: e.target.value,
                  })
                }
                placeholder="All Listing Statuses"
                options={listingStatusOptions}
              />
              <FormField
                id="createdFrom"
                label="Created From"
                type="date"
                value={searchFilters.createdFrom || ''}
                onChange={(e) =>
                  setSearchFilters({ ...searchFilters, createdFrom: e.target.value })
                }
              />
              <FormField
                id="createdTo"
                label="Created To"
                type="date"
                value={searchFilters.createdTo || ''}
                onChange={(e) =>
                  setSearchFilters({ ...searchFilters, createdTo: e.target.value })
                }
              />
            </div>
          )}

          <div className="flex space-x-4 mb-4">
            {showAdvanced && (
              <>
                <Button variant="secondary" onClick={handleClearSearch}>
                  Clear Search
                </Button>
                <Button type="submit">Search</Button>
              </>
            )}
          </div>
        </form>

        <ListingTable
          data={items}
          handleEdit={(dto) => navigate(`/admin/listings/${dto.id}`)}
          handleDelete={handleDelete}
          currentPage={currentPage}
          totalPages={Math.ceil(totalRecords / pageSize)}
          setCurrentPage={setCurrentPage}
          pageSize={pageSize}
          setPageSize={setPageSize}
          isLoading={isLoading}
          sortBy={sortBy}
          sortDescending={sortDescending}
          onSort={handleSort}
        />
      </Card>
    </AdminLayout>
  );
};

export default ListingList;
